import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { refreshSession, isTokenExpiring } from './session-actions';

const CHECK_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes

/**
 * Keep the auth session alive while the user is logged in.
 *
 * Checks the access token every 10 minutes (and whenever the window regains
 * focus) and silently refreshes it via the refresh token before it expires.
 * If the refresh token is also dead, refreshSession() expires the session
 * which auto-logs the user out and notifies them.
 *
 * storageKey defaults to the main app's key; standalone extension builds pass
 * their own (`${extConfig.id}:userDetails`, see AppExtension.js).
 */
export default function useSessionKeepAlive(storageKey = 'bodapro-user') {
    const dispatch = useDispatch();
    const { authenticated, userDetails } = useSelector(state => state.session);
    const token = userDetails?.token;

    React.useEffect(() => {
        if (!authenticated || !token) return;

        const check = () => {
            if (isTokenExpiring(token)) {
                dispatch(refreshSession(storageKey));
            }
        };

        check();
        const intervalId = setInterval(check, CHECK_INTERVAL_MS);
        window.addEventListener('focus', check);

        return () => {
            clearInterval(intervalId);
            window.removeEventListener('focus', check);
        };
    }, [authenticated, token, dispatch, storageKey]);
}
